import { useCallback, useRef } from 'react';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

interface TabAwareNavigationState {
  index: number;
  routes: { key: string }[];
  type: string;
}

interface TabAwareNavigation {
  getParent: () => TabAwareNavigation | undefined;
  getState: () => TabAwareNavigationState | undefined;
}

// Key of the focused tab, found by walking up to the nearest tab navigator
// (depth-agnostic). Returns undefined when there is no tab ancestor.
export const getFocusedTabKey = (
  navigation: TabAwareNavigation,
): string | undefined => {
  let current = navigation.getParent();
  while (current) {
    const state = current.getState();
    if (state?.type === 'tab') {
      return state.routes[state.index]?.key;
    }
    current = current.getParent();
  }
  return undefined;
};

/**
 * Calls `onTabBlur` when the user switches to a different tab, but not when
 * navigating within the same tab's stack (item detail, pushed screens) or on
 * in-screen state changes. The base screen blurs for both cases, so we compare
 * the focused tab between focus and blur instead of relying on the blur alone.
 */
export const useTabBlurListener = (onTabBlur: () => void) => {
  const navigation = useNavigation();

  // Ref so the focus effect depends only on the stable navigation object.
  const onTabBlurRef = useRef(onTabBlur);
  onTabBlurRef.current = onTabBlur;

  useFocusEffect(
    useCallback(() => {
      const ownTabKey = getFocusedTabKey(navigation);

      return () => {
        if (ownTabKey && getFocusedTabKey(navigation) !== ownTabKey) {
          onTabBlurRef.current();
        }
      };
    }, [navigation]),
  );
};
